import { useEffect, useState } from "react";
import Slider from "react-slick";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
// helpers
import { getCurrentDevice } from "../../../../common/utils/helpers/currentDeviceHelpers/getCurrentDevice";
import { type MunichCityPassOffer } from "../types";

interface MunichCityPassOfferSelectionProps {
  offers: MunichCityPassOffer[];
  selectedOffer: MunichCityPassOffer | null;
  onSelectOffer: (offer: MunichCityPassOffer) => void;
}

const slidesToShowDesktop = 5;
const slidesToShowMobile = 3;

export default function MunichCityPassOfferSelection({
  offers,
  selectedOffer,
  onSelectOffer
}: MunichCityPassOfferSelectionProps) {
  const { t } = useTranslation();

  const slidesToShow = Math.min(
    getCurrentDevice() === "phone" ? slidesToShowMobile : slidesToShowDesktop,
    offers.length
  );

  const infinite = offers.length > slidesToShow;

  const settings = {
    arrows: true,
    dots: true,
    infinite,
    speed: 500,
    slidesToShow,
    slidesToScroll: slidesToShow,
    vertical: false,
    responsive: [
      {
        breakpoint: 600,
        settings: {
          slidesToShow: Math.min(slidesToShowMobile, offers.length),
          slidesToScroll: Math.min(slidesToShowMobile, offers.length)
        }
      }
    ]
  };

  const [initialSlide, setInitialSlide] = useState(-1);

  useEffect(() => {
    // Position slider once when offers load (do not remount on every selection change).
    if (!offers?.length) {
      return;
    }

    if (selectedOffer?.offer_code) {
      const selectedIndex = offers.findIndex(offer => offer?.offer_code === selectedOffer.offer_code);
      const currentSlidesToShow = getCurrentDevice() === "phone" ? slidesToShowMobile : slidesToShowDesktop;
      const getInitialSliderIndex =
        selectedIndex >= 0 ? Math.floor(selectedIndex / currentSlidesToShow) * currentSlidesToShow : 0;
      setInitialSlide(getInitialSliderIndex);
      return;
    }

    setInitialSlide(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only when offers catalog changes
  }, [offers]);

  return (
    <Box mt={{ xs: 3, sm: 2 }} mb={1}>
      <Box className="munich-city-pass-quick-selection-title">{t("transport.munichCityPass.offerSectionTitle")}</Box>
      <Box ml={{ xs: 2, sm: 1 }} mr={{ xs: 2, sm: 1 }}>
        {initialSlide >= 0 && offers?.length > 0 && (
          <Slider {...settings} initialSlide={initialSlide}>
            {offers.map(offer => (
              <Box
                key={offer?.offer_code}
                className={`munich-city-pass-quick-selection-item ${
                  offer?.offer_code === selectedOffer?.offer_code ? "munich-city-pass-quick-selection-item__active" : ""
                }`}
                onClick={() => onSelectOffer(offer)}
                data-cy={`munich-city-pass-offer-${offer?.offer_code}`}
              >
                {offer?.name}
              </Box>
            ))}
          </Slider>
        )}
      </Box>
    </Box>
  );
}
